import React, { useEffect } from 'react'
import CompactTimelineEditor from './timeline_editor/CompactTimelineEditor'
import EnlargedTimelineEditor from './timeline_editor/EnlargedTimelineEditor'
import { editableDateRecordFromString, editableDateRecordToString } from '@utils/editable_date'
import { EditableDate } from '@app-types/entity_types'
import { IValueEditorProps } from '@app-types/general'
import { createLogger } from '@utils/logger'
import useValueEditor from '@hooks/useValueEditor'

// Initialize logger
const log = createLogger('TimelineEditor', { prependScope: true })
log.setLevel('info')

// Define interfaces for props and related data structures

// When we use the TimelineEditor component, we can pass in a value prop that is an object
// with two keys, s and e, which are strings representing dates.
export type TimelineEditorValue = {
  s?: string | null
  e?: string | null
} | null

// Internally we use a different format for the value, which is an object
// that has two keys, s and e, which are EditableDate objects or null.
export type InternalTimelineEditorValue = {
  s: EditableDate | null
  e: EditableDate | null
}

type TimelineEditorProps = Omit<
  IValueEditorProps,
  'value' | 'onChange' | 'onSave' | 'parseValue' | 'serializeValue'
> & {
  value?: TimelineEditorValue
  onChange?: (data: { name?: string; value: TimelineEditorValue | null }) => void
  onSave?: (value: TimelineEditorValue | null) => void
  mode?: 'compact' | 'enlarged'
  parseValue?: (value: TimelineEditorValue) => InternalTimelineEditorValue
  serializeValue?: (value: InternalTimelineEditorValue) => TimelineEditorValue
}

const defaultParseValueFn = (value: TimelineEditorValue): InternalTimelineEditorValue => {
  let result: InternalTimelineEditorValue = { s: null, e: null }
  if (value) {
    const { s, e } = value
    result = {
      s: s ? editableDateRecordFromString(s) : null,
      e: e ? editableDateRecordFromString(e) : null,
    }
  }
  return result
}

const defaultSerializeValueFn = (value: InternalTimelineEditorValue): TimelineEditorValue => {
  let result: TimelineEditorValue = null
  if (value) {
    const { s, e } = value
    result = {
      s: s ? editableDateRecordToString(s) : null,
      e: e ? editableDateRecordToString(e) : null,
    }
  }
  return result
}

const TimelineEditor: React.FC<TimelineEditorProps> & { EDITOR_TYPE: string } = ({
  name,
  value = null,
  onChange,
  onSave,
  mode = 'compact',
  parseValue = defaultParseValueFn,
  serializeValue = defaultSerializeValueFn,
  ...restProps
}) => {
  // Log when custom parse/serialize functions are provided
  useEffect(() => {
    log.debug(parseValue !== defaultParseValueFn && 'Custom parseValue provided')
    log.debug(serializeValue !== defaultSerializeValueFn && 'Custom serializeValue provided')
  }, [parseValue, serializeValue])

  const { localValue, handleLocalChange, handleBlurOrSave } = useValueEditor<
    TimelineEditorValue,
    InternalTimelineEditorValue
  >({
    value,
    parseValue,
    serializeValue,
    onChange: onChange ? ({ value }) => onChange({ name, value }) : undefined,
    onSave,
  })

  const handleChange = (obj: InternalTimelineEditorValue) => {
    // Merge changes into localValue before updating
    const nextLocalValue = {
      ...localValue,
      ...obj,
    }
    handleLocalChange(nextLocalValue)
  }

  const handleSave = () => {
    handleBlurOrSave()
  }

  const content =
    mode === 'compact' ? (
      <CompactTimelineEditor value={localValue} onChange={handleChange} {...restProps} />
    ) : (
      <EnlargedTimelineEditor value={localValue} onChange={handleChange} {...restProps} />
    )

  return <span className="content-editor content-editor--timeline timeline-editor">{content}</span>
}

TimelineEditor.EDITOR_TYPE = 'timeline'

export default TimelineEditor
